import { useAppSettingsContext } from "contexts/AppSettingsProvider";
import { EAppSettings } from "library/types";

/**
 * Simplified hook for accessing app settings
 * Follows HBH pattern of loading all settings at startup
 */
export function useAppSettings() {
  const {
    getSetting,
    getSettingValue,
    updateSetting,
    updateSettingOptimistic,
    settings,
    isLoading,
    isInitialized,
    hasAllSettings,
  } = useAppSettingsContext();

  return {
    // Get all settings for a specific type (returns array)
    getSettings: getSetting,

    // Get single setting value (returns first match or null)
    getSetting: getSettingValue,

    // Update a setting (pessimistic - waits for API)
    updateSetting,

    // Update a setting (optimistic - immediate UI update, like HBH)
    updateSettingOptimistic,

    // Get all user settings
    allSettings: settings,

    // Loading and initialization states
    isLoading,
    isInitialized,
    hasAllSettings,

    // Convenience getters for common settings
    theme: getSettingValue(EAppSettings.theme)?.userAppSettingValue || "main",
    isDarkMode: getSettingValue(EAppSettings.darkMode)?.userAppSettingValue === "true",

    // Convenience methods for common actions (HBH-style optimistic updates)
    toggleDarkMode: () => {
      const currentValue = getSettingValue(EAppSettings.darkMode)?.userAppSettingValue === "true";
      updateSettingOptimistic(EAppSettings.darkMode, (!currentValue).toString(), false);
    },

    setTheme: (themeName: string) => {
      updateSettingOptimistic(EAppSettings.theme, themeName, true);
    },
  };
}
